import styled from "@emotion/styled";
import { getBuildingList } from "apis/meetings/meeting-room";
import { addFloor } from "apis/resources/building";
import { useToast } from "hook/useToast";
import React from "react";
import { TBuilding } from "types/building.types";
import UI from "@mile-meeting/mile-ui";
import BuildDropdown from "./BuildDropdown";

interface RoomFloorFieldProps {
  floorId?: string;
  floorName?: string;
  buildingName?: string;
  onChange: (floorId?: string, floorName?: string) => void;
  disabled?: boolean;
  buildingId?: string;
  onUpdate?: () => void;
  buildingList?: (TBuilding | null)[];
  onChangeBuildingList?: (value: (TBuilding | null)[]) => void;
}

const RoomFloorField: React.FC<RoomFloorFieldProps> = ({
  floorId,
  floorName,
  onChange,
  disabled = false,
  buildingName,
  buildingId,
  onUpdate,
  buildingList = [],
  onChangeBuildingList,
}) => {
  const [isOpenDropdown, setIsOpenDropdown] = React.useState(false);
  const floorDropdownRef = React.useRef(false);
  const { showToast } = useToast();

  const handleAddFloor = async (name: string) => {
    if (name !== "") {
      const buildingId = buildingList.find((item) => item?.name === buildingName)?.id;
      if (buildingId) {
        const res = await addFloor(buildingId, name);
        if (res.data) {
          const buildings = await getBuildingList();
          onChangeBuildingList?.(buildings.data ?? []);
        } else {
          showToast(res.message ?? "층을 추가할 수 없습니다.", "error");
        }
      }
    }
  };

  const handleRemoveFloor = () => {
    if (disabled) return;
    onChange("", "");
  };

  const handleDropdownClick = () => {
    floorDropdownRef.current = true;
  };

  const updateBuildingList = (type: "building" | "floor", updateId: string) => {
    getBuildingList().then((res) => {
      onChangeBuildingList?.(res.data ?? []);

      if (floorId === updateId) {
        const building = res.data?.find((building) => building?.id === buildingId);
        const floor = building?.floors.find((floor) => floor?.id === updateId);
        onChange(floor?.name ?? "", "");
      } else {
        onChange(undefined, "");
      }
    });
    onUpdate?.();
  };

  return (
    <Container>
      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
        층
      </UI.Typography>
      <div style={{ position: "relative" }} onFocus={() => setIsOpenDropdown(true)}>
        <UI.InputGroup.TextField
          disabled={disabled}
          placeholder="회의실 위치 층"
          showPrefixIcon
          prefixIcon="office"
          value={floorName ?? ""}
          disabledFocus
          suffixIcon="removeCircle"
          showSuffixIcon={floorName ? true : false}
          onClickSuffixIcon={handleRemoveFloor}
        />
        {isOpenDropdown && (
          <BuildDropdown
            onUpdateBuildInfo={updateBuildingList}
            open={isOpenDropdown}
            setOpen={setIsOpenDropdown}
            type="floor"
            onAddOption={handleAddFloor}
            selected={floorName}
            onDropdownClick={handleDropdownClick}
            data={buildingList.find((item) => item?.name === buildingName)?.floors.filter((floor) => floor !== null)}
            onChange={(id, name) => {
              if (floorId === id) {
                handleRemoveFloor();
                return;
              }
              onChange(id, name);
              setIsOpenDropdown(false);
            }}
          />
        )}
      </div>
    </Container>
  );
};

export default React.memo(RoomFloorField);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;
